import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PaymentMethods } from './PaymentMethods';
import {
  AccountMovements,
  EspecialCard,
  GoldenCard,
  LoadWalletMap,
  TransferBalance,
  Header,
  ListPinsCards,
  MobileBottomActions,
  PremiumCard,
  PrintedTickets,
} from './components';
import { PayInInstallments } from './components/PayInInstallments';
import { PayInOneInstallments } from './components/PayInOneInstallment';
import { PayWithBalance } from './components/PayWithBalance';
import { useHandlePaymentMethod } from './hooks/useHandlePaymentMethod';
import { useHandleTransaction } from './hooks/useHandleTransaction';
import { useGetInitMyAccountExtra } from './hooks/useMyAccount';

import { Carousel, Button, Divider, Loader, NavBar, Modal } from '@/components';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import useAuth from '@/hooks/useAuth';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import { usePermutive } from '@/hooks/usePermutive';
import usePinsAccount from '@/hooks/usePinsAccount';
import ROUTES from '@/router/routes';
import { ResponseMiCuenta, TipoProductoTO } from '@/types/myAccount';
import {
  cn,
  CONF_TARJETAS,
  isAndroid,
  letterGenderById,
  PACKS_BY_ID,
  PACKS_BY_NAME,
} from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

/**
 * "Mis Pines"
 * ruta: ROUTES.myAccount
 */
export const MyAccount = () => {
  const { isMobile, isDesktop } = useIsDesktop();
  const { handlePaymentMethod } = useHandlePaymentMethod();
  const { handleTransactionValidation } = useHandleTransaction();

  const { data, isLoading, isFetching, refetch } = useGetInitMyAccountExtra();
  const { updatePinsData, pinsData } = usePinsAccount();
  const [mounted, setMounted] = useState(false);

  const initData = useMemo(() => {
    return data?.initData;
  }, [data?.initData]);

  const products = useMemo(() => {
    return data?.products;
  }, [data?.products]);

  const location = useLocation();
  const navigate = useNavigate();
  const especialSectionRef = useRef<HTMLDivElement | null>(null);
  useMetaData({
    title: 'Mis Pines',
  });

  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.mispines });

  const { userInitialData } = useAuth();
  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.mispines,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_recibirinfo: userInitialData?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  const pinProduct = useMemo(() => {
    if (products?.estado && products.productos) {
      return products.productos.find(
        (producto: { id: number }) => producto.id === 1
      );
    }
  }, [products]);

  const displayLoader =
    isLoading || isFetching || !mounted || pinsData.loadingTokenized;

  const premiunOffer = useMemo(() => {
    if (data?.initData.estado && data.initData.sobreescrituraPremium) {
      return data.initData.sobreescrituraPremium;
    }
  }, [data]);

  const productPremium = useMemo(() => {
    if (products?.estado && products.productos) {
      return products.productos.filter(
        (produc: { esPremium: boolean }) => produc.esPremium
      )[0];
    }
  }, [products]);

  const productsEspecials = useMemo(() => {
    if (products?.estado && products.productos) {
      return products.productos.filter(
        (produc: TipoProductoTO) =>
          !produc.esPremium &&
          produc.id >= PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_1]
      );
    }
  }, [products]);

  const productsParsed = useMemo(() => {
    if (products?.estado && products.productos && premiunOffer?.aplica) {
      return products.productos.map((product) => {
        if (pinsData.accountMoney >= product.precio) {
          return { ...product, isDisabled: false };
        }
        return { ...product, isDisabled: true };
      });
    }
    if (products?.estado && products.productos && !premiunOffer?.aplica) {
      return products.productos.map((product) => ({
        ...product,
        isDisabled: false,
      }));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, pinsData.accountMoney]);

  const canPayWithBalance = useMemo(() => {
    return productPremium
      ? pinsData.accountMoney >= productPremium.precio
      : false;
  }, [pinsData.accountMoney, productPremium]);

  useEffect(() => {
    updatePinsData({
      showPaymentModalMethod: false,
      selectedProduct: undefined,
      selectedCard: undefined,
      showModalPayInInstallments: false,
      showWalletLoadModal: false,
      selectedInstallment: undefined,
      selectedPaymentMethod: undefined,
      showModalTransferBalance: false,
      showModalWallet: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresco cuando me hago premium cuando pago con saldo
  useEffect(() => {
    refetch();
  }, [pinsData.isPremium, refetch]);

  const handleBuyByState = useCallback(
    (product: TipoProductoTO) => {
      handlePaymentMethod({
        product,
      });

      scrollTo(0, 1);

      if (isMobile && !canPayWithBalance) {
        navigate(ROUTES.paymentMethodSelection, { replace: false });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      canPayWithBalance,
      navigate,
      productsEspecials,
      isMobile,
      handlePaymentMethod,
    ]
  );

  // si debo mostrar modal de compra premium
  const premiumVerificationModal = useCallback(() => {
    if (
      [PACKS_BY_NAME.PREMIUM, PACKS_BY_NAME.PREMIUM_1].includes(
        location.state
      ) &&
      productPremium &&
      Object.values(productPremium).length > 0 &&
      !premiunOffer?.aplica
    ) {
      handleBuyByState(productPremium);
    }
  }, [handleBuyByState, location.state, productPremium, premiunOffer]);

  // si debo mostrar modal de compra pin especial
  const especialPinVerificationModal = useCallback(() => {
    if (
      [
        PACKS_BY_NAME.PIN_FECHA_ORO,
        // PACKS_BY_NAME.PIN_ESPECIAL_TDA_1,
        // PACKS_BY_NAME.PIN_ESPECIAL_TDA_2,
        // PACKS_BY_NAME.PIN_ESPECIAL_TDA_3,
      ].includes(location.state) &&
      productsEspecials &&
      productsEspecials.length > 0
    ) {
      const especialProduct = productsEspecials.find(
        (pin) =>
          pin.id === PACKS_BY_ID[location.state as keyof typeof PACKS_BY_NAME]
      );

      especialProduct && handleBuyByState(especialProduct);
    }
  }, [handleBuyByState, location.state, productsEspecials]);

  const loadInit = useCallback(() => {
    if (!isLoading && initData?.estado && !isFetching && !mounted) {
      // verifico si vengo a comprar premium o pin especial
      premiumVerificationModal();
      especialPinVerificationModal();

      // seteo datos globales
      updatePinsData({
        pinsAvailable: initData.pinesDisponibles || 0,
        accountMoney: initData.saldo || 0,
        isPremium: initData.premium || false,
      });

      setMounted(true);
    }
  }, [
    isLoading,
    initData,
    isFetching,
    mounted,
    premiumVerificationModal,
    especialPinVerificationModal,
    updatePinsData,
  ]);

  useEffect(() => {
    loadInit();

    if (location.state?.scrollToTop) {
      window.scrollTo(0, 0);
      // Clear the state after scrolling to prevent scrolling
      navigate(location.pathname, { replace: true, state: {} });
    }

    if (location.state?.scrollToEspecial && especialSectionRef.current) {
      setTimeout(() => {
        especialSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100); // pequeño delay para asegurar renderizado
      // Clear the state after scrolling to prevent scrolling
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, loadInit]);

  const isOnlyGoldenTournament = useMemo(() => {
    return (
      productsEspecials?.length === 1 &&
      productsEspecials[0].id === PACKS_BY_ID[PACKS_BY_NAME.PIN_FECHA_ORO]
    );
  }, [productsEspecials]);

  const contentEspecial = useMemo(() => {
    if (isOnlyGoldenTournament && productsEspecials) {
      return (
        <GoldenCard
          key={productsEspecials[0].id}
          product={productsEspecials[0]}
        />
      );
    } else {
      return productsEspecials?.reverse()?.map((product: TipoProductoTO) => {
        return <EspecialCard key={product.id} product={product} />;
      });
    }
  }, [isOnlyGoldenTournament, productsEspecials]);

  const classTitle = 'font-bold text-base text-gdt-gray lg:text-lg';

  const initDataAccount = data?.initData as ResponseMiCuenta;
  const hasMovements = initDataAccount?.movimientos ?? false;
  const premiumPrice = useMemo(() => {
    if (
      initData?.estado &&
      initData.valorAlQueComproPremium &&
      initData.premium
    ) {
      return Number(initData.valorAlQueComproPremium.replace('.', ''));
    }
    if (productPremium) {
      return premiunOffer?.aplica ? 0 : productPremium.precio || 0;
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiunOffer, productPremium]);

  const premiumOffer = useMemo(() => {
    if (
      initData?.estado &&
      initData.premium &&
      initData.valorAlQueComproPremium
    ) {
      return '';
    }
    if (premiunOffer?.aplica) {
      return premiunOffer.txtOferta;
    }
    if (productPremium?.cintillo && productPremium?.idTipoOferta !== 0) {
      return productPremium?.cintillo;
    }
    // (cintillo por defecto:)
    return {
      txtOferta: '¡Oportunidad única!',
      colorForeground: undefined,
      colorBackground: undefined,
    };
    // return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiunOffer, productPremium]);

  const payWithBalanceTitle = useMemo(() => {
    if (!pinsData.selectedProduct) return;
    if (
      pinsData.selectedProduct &&
      !pinsData.selectedProduct?.esPremium &&
      pinsData?.selectedProduct?.id >=
        PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_1]
    ) {
      return 'Pin Especial';
    }
    if (
      pinsData.selectedProduct?.cantidad === 1 &&
      pinsData.selectedProduct.esPremium
    ) {
      return 'Pack Premium';
    }
    return pinsData.selectedProduct?.cantidad === 1
      ? '1 PIN'
      : `Pack ${pinsData.selectedProduct?.cantidad} Pines`;
  }, [pinsData.selectedProduct]);

  // Si no debemos renderizar al redireccionar a pago de premium, retornamos null
  if (
    isMobile &&
    location.state === PACKS_BY_NAME.PREMIUM &&
    !canPayWithBalance
  ) {
    return null;
  }

  if (
    isAndroid() &&
    localStorage.getItem('disableMyAccountInAndroid') === 'true'
  ) {
    return <MyAccountDisabledAndroid />;
  }

  return (
    <NavBar
      desktop
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          title: 'Mis pines',
        },
      }}
    >
      {<Loader isVisible={displayLoader} />}
      <div className="min-h-[900px]">
        {mounted && (
          <>
            <Header
              isPremiumOffer={premiunOffer?.aplica}
              pinProduct={pinProduct}
            />
            <div className="px-4 lg:px-0">
              {/* VER OFERTA PREMIUM */}
              {products?.estado && (
                <PremiumCard
                  isPremium={pinsData.isPremium}
                  isPromotionUser={premiunOffer?.aplica || false}
                  premiumOffer={premiumOffer}
                  price={premiumPrice}
                  product={productPremium}
                  promotionUrl={premiunOffer?.landing || ''}
                />
              )}
              {/* IR A BENEFICIOS PREMIUM */}
              {isMobile && (
                <article className="text-center mt-5">
                  <Button
                    className="text-gdt-blue underline underline-offset-4 font-bold cursor-pointer"
                    href={ROUTES.premiumBenefits}
                    type="link"
                    variant="text"
                  >
                    Beneficios de ser Premium
                  </Button>
                </article>
              )}

              <Divider className="mt-6 mb-7 lg:hidden" type="horizontal" />

              <section className="max-w-[1100px] mx-auto w-full">
                {/* LISTADO DE PACKS DE PINES */}
                <h2 className={cn(classTitle, 'lg:mt-14')}>Pack de Pines</h2>
                {products?.estado && products.productos && (
                  <ListPinsCards products={productsParsed} />
                )}

                {/* TICKETS IMPRESOS */}
                {isMobile && <PrintedTickets />}

                <Divider className="mt-6 mb-8 lg:hidden" type="horizontal" />

                {/* PINES ESPECIALES */}
                <article ref={especialSectionRef}>
                  {contentEspecial &&
                    productsEspecials &&
                    productsEspecials?.length > 0 && (
                      <h2 className={cn(classTitle, 'lg:mt-12 mb-4 lg:mb-8')}>
                        {isOnlyGoldenTournament
                          ? 'Pin especial'
                          : 'Pines Especiales'}
                      </h2>
                    )}
                  {!isOnlyGoldenTournament &&
                    (isMobile ? (
                      <Carousel>{contentEspecial}</Carousel>
                    ) : (
                      <div
                        className={cn('flex justify-between lg:justify-start', {
                          'gap-x-11':
                            productsEspecials && productsEspecials?.length <= 3,
                        })}
                      >
                        {contentEspecial}
                      </div>
                    ))}
                  {isOnlyGoldenTournament && contentEspecial}
                </article>

                {/* MOVIMIENTOS Y KIOSCOS DESKTOP */}
                {isDesktop && (
                  <article className="mt-20">
                    <div className="flex justify-between gap-x-[3%]">
                      {hasMovements && (
                        <div className="flex flex-col flex-1">
                          <h2 className={classTitle}>
                            Movimientos de mi cuenta
                          </h2>
                          <div className="mt-12">
                            <AccountMovements />
                          </div>
                        </div>
                      )}
                      <div
                        className={cn(['flex flex-col flex-1 !h-[595px]'])}
                        id="hash-map"
                      >
                        <h2 className={classTitle}>
                          Kioscos con carga Virtual
                        </h2>
                        <p className="my-3">
                          Encontrá los puntos de venta más cercanos a tu
                          domicilio.
                        </p>
                        <LoadWalletMap data={data?.initData} />
                      </div>
                    </div>
                    <footer className="hidden lg:flex justify-between space-x-6 mb-12 mt-14">
                      <AdSlot divId={ADS_DIV_ID.caja1} variant="BOX" />
                      {isDesktop && (
                        <>
                          <AdSlot divId={ADS_DIV_ID.caja2} variant="BOX" />
                          <AdSlot divId={ADS_DIV_ID.caja3} variant="BOX" />
                        </>
                      )}
                    </footer>
                  </article>
                )}
              </section>

              {/* ACCIONES MOBILE */}
              {isMobile && <MobileBottomActions data={data?.initData} />}

              <Modal
                controlledOnClose={() =>
                  updatePinsData({
                    showModalTransferBalance: false,
                  })
                }
                isControlledOpen={pinsData.showModalTransferBalance ?? false}
                render={() => (
                  <TransferBalance
                    closeModal={() =>
                      updatePinsData({
                        showModalTransferBalance: false,
                      })
                    }
                  />
                )}
                size="xs"
                variant="controlled"
              />
            </div>

            {Boolean(pinsData.showPaymentModalMethod) && <PaymentMethods />}
            <Modal
              controlledOnClose={() =>
                updatePinsData({ showModalPayWithBalance: false })
              }
              isControlledOpen={Boolean(
                pinsData.showModalPayWithBalance && mounted
              )}
              render={() => {
                if (
                  pinsData.selectedProduct &&
                  products?.estado &&
                  products.productos &&
                  mounted
                ) {
                  return (
                    <PayWithBalance
                      accountMoney={pinsData.accountMoney}
                      mounted={mounted}
                      packs={
                        products.productos.map((p) => p.cantidad ?? 0) || []
                      }
                      pinsAvailable={pinsData.pinsAvailable}
                      selectedProduct={pinsData.selectedProduct}
                    />
                  );
                }
              }}
              size="xs"
              title={payWithBalanceTitle}
              variant="controlled"
            />
            <Modal
              controlledOnClose={() => {
                updatePinsData({
                  statusTransaction: { status: null, msg: undefined },
                });
              }}
              isControlledOpen={pinsData?.statusTransaction?.status === 'error'}
              render={() => {
                return (
                  <>
                    <p>
                      <br />
                      {pinsData?.statusTransaction?.msg}
                    </p>
                    <div className="flex  space-x-2 mt-10">
                      <Button
                        className="flex-1"
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          updatePinsData({
                            selectedCard: undefined,
                            showModalWallet: false,
                            showPaymentModalMethod: false,
                            showWalletLoadModal: false,
                            moneyToWallet: undefined,
                            statusTransaction: { status: null, msg: undefined },
                          });
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        className="flex-1"
                        type="button"
                        variant="primary"
                        onClick={() => {
                          return (
                            pinsData.selectedCard &&
                            handleTransactionValidation(
                              pinsData.selectedCard,
                              true
                            )
                          );
                        }}
                      >
                        Aceptar
                      </Button>
                    </div>
                  </>
                );
              }}
              size="xs"
              title="Atención"
              variant="controlled"
            />
            <Modal
              bottomSheetClassName="bg-gdt-gray-7"
              className="bg-gdt-gray-7"
              controlledOnClose={() => {
                updatePinsData({
                  showModalPayInInstallments: false,
                  statusTransaction: { status: null, msg: undefined },
                  showPaymentModalMethod: true,
                  tokenizedCard: undefined,
                  responseTokenizedCards: undefined,
                });
              }}
              headerSheetClassName="rounded-t-lg !bg-gdt-gray-7"
              isControlledOpen={Boolean(pinsData.showModalPayInInstallments)}
              render={() => (
                <PayInInstallments
                  handleContinue={() =>
                    pinsData.selectedCard &&
                    handleTransactionValidation(pinsData.selectedCard, true)
                  }
                  selectedCard={pinsData.selectedCard || 'CREDITO'}
                />
              )}
              size="xs"
              title={CONF_TARJETAS[pinsData.selectedCard ?? 'CREDITO'].nombre}
              variant="controlled"
            />
            <Modal
              bottomSheetClassName="bg-gdt-gray-7"
              className="bg-gdt-gray-7"
              controlledOnClose={() => {
                updatePinsData({
                  showModalPayInOneInstallment: false,
                  statusTransaction: { status: null, msg: undefined },
                  showPaymentModalMethod: true,
                  tokenizedCard: undefined,
                  responseTokenizedCards: undefined,
                });
              }}
              headerSheetClassName="rounded-t-lg !bg-gdt-gray-7"
              isControlledOpen={Boolean(pinsData.showModalPayInOneInstallment)}
              render={() => (
                <PayInOneInstallments
                  handleContinue={() =>
                    pinsData.selectedCard &&
                    handleTransactionValidation(pinsData.selectedCard, true)
                  }
                  selectedCard={pinsData.selectedCard || 'CREDITO'}
                />
              )}
              size="xs"
              title={CONF_TARJETAS[pinsData.selectedCard ?? 'CREDITO'].nombre}
              variant="controlled"
            />
          </>
        )}
      </div>
    </NavBar>
  );
};

/**
 * Muestra
 * "Para ingresar a esta sección es necesario hacerlo por navegador."
 */
function MyAccountDisabledAndroid() {
  return (
    <NavBar
      desktop
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          title: 'Mis Pines',
        },
      }}
    >
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-lg text-center">
          Para ingresar a esta sección es necesario hacerlo por navegador.
        </p>
      </div>
    </NavBar>
  );
}
